import React, { useCallback, useMemo } from 'react'
import { useIntl } from 'react-intl'
import { Box, Chip } from '@mui/material'

import { ReactComponent as UserOwner } from '@mike/mike-shared-frontend/media/icons/UserOwner'
import { ReactComponent as UserContributor } from '@mike/mike-shared-frontend/media/icons/UserContributor'
import { ReactComponent as UserReader } from '@mike/mike-shared-frontend/media/icons/UserReader'
import { FolderTreeRow, UserTreeRow } from '../../helpers/memberReport'
import TreeTable, { TreeColumnDef } from './TreeTable'

const ROLE_ICONS = {
  Owner: UserOwner,
  Contributor: UserContributor,
  Reader: UserReader
}

const RoleCell = ({ role }: { role?: string }) => {
  const intl = useIntl()
  if (!role) return null
  const Icon = ROLE_ICONS[role]
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      {Icon && <Icon />}
      {intl.formatMessage({ id: `common.roles.${role}` })}
    </Box>
  )
}

const EmailCell = ({ row }: { row: FolderTreeRow | UserTreeRow }) => {
  const intl = useIntl()
  if (row.unknown) {
    return (
      <Chip
        size="small"
        color="warning"
        variant="outlined"
        label={intl.formatMessage({ id: 'memberReport.unknownIdentity' })}
      />
    )
  }
  return <>{row.email}</>
}

const useNameFormatter = () => {
  const intl = useIntl()
  return useCallback(
    (row: FolderTreeRow | UserTreeRow) => {
      const name =
        row.unknown && !row.name
          ? intl.formatMessage({ id: 'memberReport.unknownUser' })
          : row.name
      return row.occurrences && row.occurrences > 1
        ? intl.formatMessage(
            { id: 'memberReport.mergedFolders' },
            { name, count: row.occurrences }
          )
        : name
    },
    [intl]
  )
}

const nameMeta = { width: '40%', minWidth: 280 }
const emailMeta = { width: '35%', minWidth: 200 }
const roleMeta = { width: '15%', minWidth: 140 }
const countMeta = { width: 110 }

export const FolderTreeGrid = ({
  rows,
  expandAll
}: {
  rows: FolderTreeRow[]
  expandAll: boolean
}) => {
  const intl = useIntl()
  const formatName = useNameFormatter()

  const columns = useMemo<TreeColumnDef<FolderTreeRow>[]>(
    () => [
      {
        id: 'name',
        header: intl.formatMessage({ id: 'memberReport.column.folder' }),
        accessorFn: row => formatName(row),
        sortFn: 'alphanumeric',
        meta: nameMeta
      },
      {
        id: 'email',
        header: intl.formatMessage({ id: 'memberReport.column.email' }),
        accessorFn: row => (row.type === 'member' ? row.email : undefined),
        cell: ({ row }) =>
          row.original.type === 'member' ? (
            <EmailCell row={row.original} />
          ) : null,
        sortFn: 'alphanumeric',
        sortUndefined: 'last',
        meta: emailMeta
      },
      {
        id: 'role',
        header: intl.formatMessage({ id: 'memberReport.column.role' }),
        accessorFn: row => row.role,
        cell: ({ getValue }) => <RoleCell role={getValue()} />,
        sortFn: 'alphanumeric',
        sortUndefined: 'last',
        meta: roleMeta
      },
      {
        id: 'memberCount',
        header: intl.formatMessage({ id: 'memberReport.column.users' }),
        accessorFn: row =>
          row.type === 'folder' ? row.memberCount : undefined,
        sortFn: 'basic',
        sortUndefined: 'last',
        meta: countMeta
      }
    ],
    [intl, formatName]
  )

  return (
    <TreeTable
      rows={rows}
      columns={columns}
      expandedDepth={expandAll ? -1 : 1}
      emptyMessage={intl.formatMessage({ id: 'memberReport.noResults' })}
    />
  )
}

export const UserTreeGrid = ({
  rows,
  expandAll
}: {
  rows: UserTreeRow[]
  expandAll: boolean
}) => {
  const intl = useIntl()
  const formatName = useNameFormatter()

  const columns = useMemo<TreeColumnDef<UserTreeRow>[]>(
    () => [
      {
        id: 'name',
        header: intl.formatMessage({ id: 'memberReport.column.user' }),
        accessorFn: row => formatName(row),
        sortFn: 'alphanumeric',
        meta: nameMeta
      },
      {
        id: 'email',
        header: intl.formatMessage({ id: 'memberReport.column.email' }),
        accessorFn: row => (row.type === 'user' ? row.email : undefined),
        cell: ({ row }) =>
          row.original.type === 'user' ? (
            <EmailCell row={row.original} />
          ) : null,
        sortFn: 'alphanumeric',
        sortUndefined: 'last',
        meta: emailMeta
      },
      {
        id: 'role',
        header: intl.formatMessage({ id: 'memberReport.column.role' }),
        // Users show their strongest role; folders the role granted there.
        accessorFn: row => (row.type === 'user' ? row.highestRole : row.role),
        cell: ({ getValue }) => <RoleCell role={getValue()} />,
        sortFn: 'alphanumeric',
        sortUndefined: 'last',
        meta: roleMeta
      }
    ],
    [intl, formatName]
  )

  return (
    <TreeTable
      rows={rows}
      columns={columns}
      expandedDepth={expandAll ? -1 : 0}
      emptyMessage={intl.formatMessage({ id: 'memberReport.noResults' })}
    />
  )
}
